import type { FC } from 'react';
import { useCallback, useRef } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { CreativeAccordionDetails } from 'features/creatives';
import { CreativeDateTypography } from '../../creatives/components/CreativeComponents';
import { LinkOutlined } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import copy from 'copy-to-clipboard';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';

interface GoogleAccordionDetailsProps {
  domains: string[];
  firstShownOn: string; // yyyy-MM-dd
  lastShownOn: string; // yyyy-MM-dd
  clickUrl?: string;
}

const GoogleAccordionDetails: FC<GoogleAccordionDetailsProps> = ({
  domains,
  firstShownOn,
  lastShownOn,
  clickUrl,
}) => {
  const { onShowAlert } = useEnqueueSnackbar();
  const ref = useRef<HTMLDivElement>();
  const domainValue = domains.join(', ');

  const handleCopyScrapedLink = useCallback(() => {
    if (!clickUrl) return;
    copy(clickUrl);
    onShowAlert(Date.now(), 'Landing page link has been copied to clipboard!');
  }, [clickUrl, onShowAlert]);

  return (
    <CreativeAccordionDetails ref={ref}>
      <Box>
        <Typography
          sx={(t) => ({
            fontSize: t.typography.caption.fontSize,
            color: t.palette.text.primary,
            textAlign: 'left',
          })}
        >
          Domain name: {domainValue || 'Unknown domain'}
        </Typography>
      </Box>
      <Box
        sx={(t) => ({
          pb: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'start',
          borderBottom: `1px solid ${t.palette.divider}`,
          width: '100%',
          textAlign: 'left',
          gap: 0.5,
        })}
      >
        <Box sx={{ display: 'flex', justifyContent: 'start' }}>
          <CreativeDateTypography color="secondary">
            First shown:{' '}
            <CreativeDateTypography color="primary">
              {format(parseISO(firstShownOn), 'dd.MM.yyyy')}
            </CreativeDateTypography>
          </CreativeDateTypography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'start' }}>
          <CreativeDateTypography color="secondary">
            Last shown:{' '}
            <CreativeDateTypography color="primary">
              {format(parseISO(lastShownOn), 'dd.MM.yyyy')}
            </CreativeDateTypography>
          </CreativeDateTypography>
        </Box>
      </Box>
      <Button variant="text" sx={{ p: 0, textTransform: 'initial' }} disabled={!clickUrl}>
        <Tooltip title={clickUrl ?? 'No landing page link'}>
          <Typography
            onClick={handleCopyScrapedLink}
            variant="caption"
            sx={(t) => ({
              width: ref.current?.getBoundingClientRect().width,
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              color: t.palette.text.disabled,
              textAlign: 'left',
              lineHeight: '1.25',
              minWidth: 0,
            })}
          >
            <LinkOutlined sx={(t) => ({ fontSize: 18, color: t.palette.text.disabled })} />
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {clickUrl ?? 'No landing page link'}
            </Box>
          </Typography>
        </Tooltip>
      </Button>
    </CreativeAccordionDetails>
  );
};

export default GoogleAccordionDetails;
